<script lang="ts">
    import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
    import type { IconDefinition as ID1 } from '@fortawesome/free-solid-svg-icons'
    import type { IconDefinition as ID2 } from '@fortawesome/free-brands-svg-icons'
    import { createEventDispatcher, tick } from 'svelte'
    const dispatch = createEventDispatcher()
    import Fa from 'svelte-fa'
    let className = ''
    export { className as class }
    export let label = ''
    export let color: 'green' | 'red' | 'yellow' | 'secondary' | 'primary' = 'secondary'
    export let skin: 'default' | 'soft' | 'outline' | 'none' = 'default'
    export let icon: ID1 | ID2 = null
    export let loading = false
    export let mobileHideLabel = false
    export let mobileHideIcon = false
    export let grow = false
    export let growM = false
    export let help = ''
    // nome do botão quando ele fica só com o ícone (linhas compactas do PDV, do orçamento e da nota):
    // vira o title nativo, que o navegador mostra no hover e o leitor de tela anuncia
    export let title: string = null
    export let disabled = false
    export let width: string = null
    // Estado de "escolhido agora" (usado pelo inlineselect_2). É um eixo à parte da cor: a cor diz
    // que tipo de ação o botão é, active diz se ele está selecionado — o preenchimento usa a cor dele.
    export let active = false
    // MICRO-INTERAÇÃO — em avaliação, veja todas lado a lado em /debug?showcase=1
    //   'none'    o de hoje: o fundo pisca no clique e a cor leva meio segundo para mudar
    //   'press'   afunda no clique (90ms), com tempos de botão (140ms)
    //   'ripple'  press + a ondulação que nasce no ponto em que o dedo tocou
    //   'cor'     press + a cor entra no hover: o botão se preenche com a própria cor e a borda acende
    //   'brilho'  cor + uma faixa de luz que atravessa o botão quando o mouse chega
    //   'foco'    cor + um halo da cor do botão que segue o cursor por dentro dele, e onda no clique
    // Só 'transform', 'opacity' e cor se movem — nada que obrigue o navegador a recalcular a página,
    // o que importa numa tabela com dezenas de botões. Quem pediu menos movimento no sistema
    // (prefers-reduced-motion) fica só com a mudança de cor, sem movimento nenhum.
    export let motion: 'none' | 'press' | 'ripple' | 'cor' | 'brilho' | 'foco' = 'none'

    let isMobile = screen.width < 900

    if (className.includes('btn-primary')) color = 'primary'
    if (className.includes('btn-secondary')) color = 'secondary'

    function onClick() {
        if (disabled) return
        if (loading) return
        dispatch('click')
    }

    // a ondulação é o único pedaço que precisa de JavaScript: o resto é transição no css_z.css.
    // nasce onde o dedo tocou e se apaga sozinha; o .zbutton já tem overflow hidden, então ela não
    // vaza do botão. Web Animations API, nativa no Chromium do app — sem biblioteca.
    function onPointerDown(ev: PointerEvent) {
        if ((motion !== 'ripple' && motion !== 'foco') || disabled || loading) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        const button = ev.currentTarget as HTMLElement
        const rect = button.getBoundingClientRect()
        const ink = document.createElement('span')
        const size = Math.max(rect.width, rect.height) * 2
        ink.className = 'zbutton_ink'
        ink.style.width = size + 'px'
        ink.style.height = size + 'px'
        ink.style.left = ev.clientX - rect.left + 'px'
        ink.style.top = ev.clientY - rect.top + 'px'
        button.appendChild(ink)
        const animacao = ink.animate(
            [
                { transform: 'translate(-50%, -50%) scale(0)', opacity: 0.28 },
                { transform: 'translate(-50%, -50%) scale(1)', opacity: 0 },
            ],
            { duration: 520, easing: 'cubic-bezier(0.2, 0.6, 0.3, 1)' },
        )
        animacao.onfinish = () => ink.remove()
        animacao.oncancel = () => ink.remove()
    }

    // 'foco': o halo é desenhado pelo CSS num gradiente radial, e daqui vai só onde ele fica — duas
    // variáveis com a posição do cursor dentro do botão. A leitura é adiada para o próximo quadro
    // (requestAnimationFrame) porque o mouse dispara muito mais eventos do que a tela desenha.
    let quadroPendente = 0
    function onPointerMove(ev: PointerEvent) {
        if (motion !== 'foco' || disabled || loading) return
        if (quadroPendente) return
        const button = ev.currentTarget as HTMLElement
        const x = ev.clientX
        const y = ev.clientY
        quadroPendente = requestAnimationFrame(() => {
            quadroPendente = 0
            const rect = button.getBoundingClientRect()
            button.style.setProperty('--zb-mx', x - rect.left + 'px')
            button.style.setProperty('--zb-my', y - rect.top + 'px')
        })
    }

    let tooltip
    let showTooltip = false
    async function goShowTooltip(ev) {
        showTooltip = !showTooltip
        if (!showTooltip) return
        await tick()

        tooltip.style.top = 0
        tooltip.style.left = 0
        let top = ev.clientY - tooltip.clientHeight
        let left = ev.clientX - tooltip.clientWidth
        if (left + tooltip.clientWidth > window.innerWidth - 8) {
            left = window.innerWidth - 8
        }
        if (left - 16 < 0) left = 24
        tooltip.style.top = top - 24 + 'px'
        tooltip.style.left = left - 16 + 'px'
    }
</script>

<button
    on:click={onClick}
    on:pointerdown={onPointerDown}
    on:pointermove={onPointerMove}
    title={title || label || null}
    class="zbutton zbutton_{skin} zbutton_motion-{motion} is-{color} is-active-{active} is-disabled-{disabled} is-loading-{loading} zbutton-grow-{grow} zbutton-grow-mobile-{growM} {className}"
    style={width ? 'width:' + width : ''}
>
    {#if loading}
        <div class="zbutton-spinner" />
    {/if}
    <div class="zbutton-light is-{color}"></div>
    {#if icon && (!isMobile || !mobileHideIcon)}
        <div style="" class="zbutton-icon">
            <Fa {icon} />
        </div>
    {/if}
    {#if label && (!isMobile || !mobileHideLabel)}
        <div class="zbutton-label">{label}</div>
    {/if}
    {#if help}
        <div class="zbutton_tooltip_outer h-mobile">
            <div class="zinput_tooltip zbutton_tooltip" on:click|stopPropagation={goShowTooltip}>
                <Fa icon={faQuestionCircle}></Fa>
            </div>
        </div>
    {/if}
    {#if showTooltip}
        <div bind:this={tooltip} on:click|stopPropagation={goShowTooltip} class="zinput_tooltip_content zinput_tooltip_content-show-{showTooltip}">
            {help}
            <div class="zinput_tooltip_content_outer" on:mouseout={goShowTooltip} on:click|stopPropagation={goShowTooltip}></div>
        </div>
    {/if}
</button>
